import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Button,
  Flex,
  Image,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  AdminEvent,
  EventStatus,
  useEndEventMutation,
  useListEventsQuery,
} from "api";
import { useAppSelector } from "store/hooks";
import { getRouteByName } from "libs";
import { getErrorMessage } from "components";

/**
 * Admin events list. Recency-ordered with live first. The "end now"
 * action is admin-only and irreversible (purges RTDB chat); we wrap
 * it in a confirmation modal.
 */
const STATUS_LABEL: Record<EventStatus, { color: string; label: string }> = {
  scheduled: { color: "blue", label: "Programat" },
  live: { color: "red", label: "În direct" },
  ended: { color: "default", label: "Încheiat" },
  purged: { color: "default", label: "Curățat" },
};

export const EventsPage = () => {
  const navigate = useNavigate();
  const { modal, notification } = App.useApp();
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);

  const { isAll, isEventsRead, isEventsEdit } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions,
  );

  const canRead = isAll || isEventsRead || isEventsEdit;
  const canEdit = isAll || isEventsEdit;

  const { data, isLoading } = useListEventsQuery(
    { page, perPage },
    { skip: !canRead },
  );

  const [endEvent, { isLoading: isEnding }] = useEndEventMutation();

  useEffect(() => {
    if (!canRead) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [canRead, navigate]);

  const handleEnd = (row: AdminEvent) => {
    modal.confirm({
      title: `Închei evenimentul "${row.title}"?`,
      content:
        "Chat-ul live se șterge imediat și nu mai poate fi recuperat. Mediile rămân 24h pentru moderare apoi se șterg automat.",
      okText: "Închide eveniment",
      okButtonProps: { danger: true },
      cancelText: "Anulează",
      onOk: async () => {
        try {
          await endEvent(row.id).unwrap();
          notification.success({ message: "Evenimentul a fost închis." });
        } catch (e) {
          notification.error({ message: getErrorMessage(e as never) });
        }
      },
    });
  };

  const columns: ColumnsType<AdminEvent> = [
    {
      title: "Banner",
      dataIndex: "bannerUrl",
      width: 96,
      render: (url: string | null) =>
        url ? (
          <Image
            src={url}
            width={64}
            height={40}
            style={{ objectFit: "cover", borderRadius: 6 }}
            preview={false}
          />
        ) : (
          "—"
        ),
    },
    {
      title: "Titlu",
      dataIndex: "title",
      render: (title: string, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{title}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            📍 {row.locationLabel}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status: EventStatus) => (
        <Tag color={STATUS_LABEL[status].color}>
          {STATUS_LABEL[status].label}
        </Tag>
      ),
    },
    {
      title: "Început",
      dataIndex: "startsAt",
      width: 160,
      render: (val: string) => new Date(val).toLocaleString("ro-RO"),
    },
    {
      title: "Sfârșit",
      dataIndex: "endsAt",
      width: 160,
      render: (val: string) => new Date(val).toLocaleString("ro-RO"),
    },
    {
      title: "Participanți",
      dataIndex: "participantsCount",
      width: 110,
      align: "center",
    },
    {
      title: "Acțiuni",
      width: 220,
      render: (_, row) => (
        <Space>
          <Button
            size="small"
            onClick={() =>
              navigate(getRouteByName("EventEditPage", { id: row.id }))
            }
          >
            Editează
          </Button>
          {canEdit &&
            row.status !== "ended" &&
            row.status !== "purged" && (
              <Button
                size="small"
                danger
                loading={isEnding}
                onClick={() => handleEnd(row)}
              >
                Închide
              </Button>
            )}
        </Space>
      ),
    },
  ];

  if (isLoading && !data) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  return (
    <Flex vertical gap={16}>
      <Flex
        justify="space-between"
        align="center"
        gap={16}
        wrap="wrap"
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          Evenimente live
        </Typography.Title>
        {canEdit && (
          <Button
            type="primary"
            onClick={() => navigate(getRouteByName("EventCreatePage"))}
          >
            Eveniment nou
          </Button>
        )}
      </Flex>

      <Table<AdminEvent>
        columns={columns}
        dataSource={data?.data ?? []}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: perPage,
          total: Number(data?.pagination.total ?? 0),
          showSizeChanger: false,
          onChange: setPage,
        }}
        scroll={{ x: 1100 }}
      />
    </Flex>
  );
};
